import React from 'react';
import * as Inovx from '@shared';
import ContextMenu from '@facility/viewers/components/ContextMenu';
import OperationProgressDialog from '../OperationProgressDialog';
import AssetClassDetailsSection from './AssetClassDetailsSection';
import AssetSelectionSection from './AssetSelectionSection';
import ConfigurationActions from './ConfigurationActions';
import ConfigurationDialogs from './ConfigurationDialogs';
import AssetTagCsvImportDialog from './AssetTagCsvImportDialog';
import GeneralConfigurationSection from './GeneralConfigurationSection';
import useConfigurationForm from './hooks/useConfigurationForm';
import { ConfigurationFormProps } from './types';

var InovxControls = Inovx.Shared.Controls;

declare var InovxLoader: any;

const ConfigurationForm: React.FC<ConfigurationFormProps> = () => {
    const {
        isActionInProgress,
        actionInProgressMsg,
        hasSchemaChanges,
        canSaveConfiguration,
        canDeleteSchema,
        configuration,
        selectedTreeNode,
        treeDataSource,
        treeGridRenderKey,
        treeGridRef,
        treeContextMenu,
        csvImportNode,
        closeCsvImport,
        schemaUsageList,
        schemaExtensionParentNames,
        schemaDetailsSectionProps,
        assetClassDetailsSectionProps,
        selectedAssetClassTab,
        saveConfiguration,
        deleteSchema,
        cancel,
        onAddSchema,
        setSelectedAssetClassTab,
        handleTreeRowSelecting,
        handleTreeRowSelected,
        handleTreeRowDataBound,
        handleTreeRowExpanded,
        handleTreeRowCollapsed,
        hideTreeContextMenu,
        openTreeContextMenu,
        getTreeContextMenuItems,
        handleTreeContextMenuItemClick,
        formFieldsSectionProps,
        externalAttributesSectionProps,
        validationSettingsSectionProps,
        importMappingSectionProps,
        dialogsProps
    } = useConfigurationForm();

    const header = InovxLoader.t('facility:form.schema');
    const renderRightPanel = () => {
        if (!selectedTreeNode) {
            return <div className="configuration-form-empty-state">{InovxLoader.t('facility:form.facilitySchema')}</div>;
        }

        if (selectedTreeNode.NodeType === 'assetClass' || selectedTreeNode.NodeType === 'assetClassExtension' || selectedTreeNode.NodeType === 'annotationClass') {
            return (
                <AssetClassDetailsSection
                    node={selectedTreeNode}
                    configuration={configuration}
                    selectedAssetClassTab={selectedAssetClassTab}
                    onSelectedAssetClassTabChange={setSelectedAssetClassTab}
                    formFieldsSection={formFieldsSectionProps}
                    externalAttributesSection={externalAttributesSectionProps}
                    validationSettingsSection={validationSettingsSectionProps}
                    importMappingSection={importMappingSectionProps}
                    {...assetClassDetailsSectionProps}
                />
            );
        }

        return (
            <GeneralConfigurationSection
                node={selectedTreeNode}
                schemaUsageList={schemaUsageList}
                schemaExtensionParentNames={schemaExtensionParentNames}
                {...schemaDetailsSectionProps}
            />
        );
    };

    return (
        <div className="facility-hierarchy-form configuration-form-shell">
            <InovxControls.StaticHeaderForm
                title={header}
                saveOnClick={saveConfiguration}
                saveBtnId="configform-save-btn"
                deleteOnClick={canDeleteSchema ? deleteSchema : null}
                deleteBtnId={canDeleteSchema ? "configform-delete-btn" : null}
                cancelOnClick={cancel}
                cancelBtnId="configform-cancel-btn"
                saveDisabled={isActionInProgress || !canSaveConfiguration || !hasSchemaChanges}
                cancelDisabled={isActionInProgress || !hasSchemaChanges}
            >
                <ConfigurationActions onAddSchema={onAddSchema} />
                <OperationProgressDialog visible={isActionInProgress} message={actionInProgressMsg} />

                <div className="configuration-form-panels">
                    <AssetSelectionSection
                        treeGridRef={treeGridRef}
                        treeDataSource={treeDataSource}
                        treeGridRenderKey={treeGridRenderKey}
                        onRowSelecting={handleTreeRowSelecting}
                        onRowSelected={handleTreeRowSelected}
                        onRowDataBound={handleTreeRowDataBound}
                        onRowExpanded={handleTreeRowExpanded}
                        onRowCollapsed={handleTreeRowCollapsed}
                        onOpenContextMenu={openTreeContextMenu}
                    />
                    <div className="configuration-form-right-panel">
                        {renderRightPanel()}
                    </div>
                </div>

                {treeContextMenu.visible && treeContextMenu.node && (
                    <ContextMenu
                        left={treeContextMenu.left}
                        top={treeContextMenu.top}
                        items={getTreeContextMenuItems(treeContextMenu.node)}
                        onItemClick={handleTreeContextMenuItemClick}
                        onRequestClose={hideTreeContextMenu}
                    />
                )}

                <ConfigurationDialogs {...dialogsProps} />
                {csvImportNode && <AssetTagCsvImportDialog node={csvImportNode} onClose={closeCsvImport} />}
            </InovxControls.StaticHeaderForm>
        </div>
    );
};

export default ConfigurationForm;
